import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {SchedulerConfigModel} from '../../models/scheduler-config.model';
import {ORDERED_WEEK_DAYS, WeekDaysEnum} from '../../enums/week-days.enum';
import {NgFor, NgIf} from '@angular/common';
import {MatIcon} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatInputModule} from '@angular/material/input';
import {ScheduleEntryModel} from '../../models/scheduler-entry.model';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {SchedulerDayBirthdayModel} from '../../models/scheduler-day-birthday.model';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {SnackBarService} from '@overflo1/frame-frontend';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {provideLuxonDateAdapter} from "@angular/material-luxon-adapter";
import {MAT_DATE_LOCALE} from "@angular/material/core";

@Component({
  selector: 'app-machine-scheduler',
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    MatIcon,
    MatButtonModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatDatepickerModule,
  ],
  providers: [
    provideLuxonDateAdapter(),
    {provide: MAT_DATE_LOCALE, useValue: 'it-IT'},
  ],
  templateUrl: './machine-scheduler.component.html',
  styleUrl: './machine-scheduler.component.scss'
})
export class MachineSchedulerComponent implements OnInit {

  @Input() schedulerEnabled: boolean = false;
  @Output() schedulerEnabledChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() schedulerConfig: SchedulerConfigModel;

  readonly WeekDaysEnum = WeekDaysEnum;
  readonly ORDERED_WEEK_DAYS: (keyof typeof WeekDaysEnum)[] = ORDERED_WEEK_DAYS;

  constructor(
    private snackBarService: SnackBarService,
  ) {
  }

  async ngOnInit(): Promise<void> {
    if (!this.schedulerConfig || !Object.keys(this.schedulerConfig).length) {
      this.schedulerConfig = new SchedulerConfigModel();
    }
  }

  addTimeSlot(weekDay: keyof typeof WeekDaysEnum): void {
    const timeSlot = new ScheduleEntryModel();
    this.schedulerConfig[weekDay].schedule.push(timeSlot);
  }

  removeTimeSlot(weekDay: keyof typeof WeekDaysEnum, scheduleItemIndex: number): void {
    this.schedulerConfig[weekDay].schedule.splice(scheduleItemIndex, 1);
  }

  replicateSchedule(weekDay: keyof typeof WeekDaysEnum): void {
    const scheduleToReplicate = this.schedulerConfig[weekDay].schedule;
    for (const day of this.ORDERED_WEEK_DAYS) {
      this.schedulerConfig[day].schedule = scheduleToReplicate.map(item => ({
        startHour: item.startHour,
        endHour: item.endHour
      }));
    }
  }

  addBirthday(weekDay: keyof typeof WeekDaysEnum): void {
    const birthday = new SchedulerDayBirthdayModel();
    this.schedulerConfig[weekDay].birthday = birthday;
    this.addBirthdayTimeSlot(weekDay);
  }

  removeBirthday(weekDay: keyof typeof WeekDaysEnum): void {
    this.schedulerConfig[weekDay].birthday = undefined;
  }

  addBirthdayTimeSlot(weekDay: keyof typeof WeekDaysEnum): void {
    const timeSlot = new ScheduleEntryModel();
    this.schedulerConfig[weekDay].birthday.schedule.push(timeSlot);
  }

  removeBirthdayTimeSlot(weekDay: keyof typeof WeekDaysEnum, birthdayScheduleIndex: number): void {
    this.schedulerConfig[weekDay].birthday.schedule.splice(birthdayScheduleIndex, 1);
  }

  toggleScheduler(): void {
    this.schedulerEnabled = !this.schedulerEnabled;
    this.schedulerEnabledChange.emit(this.schedulerEnabled);
  }

  get() {
    return {
      schedulerEnabled: this.schedulerEnabled,
      schedulerConfig: this.schedulerConfig,
    }
  }

  validateSchedulerConfig(): boolean {
    if (
      !this.schedulerConfig ||
      !this.validTimeSlotsOverlap() ||
      !this.validStartHourBeforeEndHour()
    ) {
      return false;
    }
    return true;
  }

  validTimeSlotsOverlap(): boolean {
    // check if schedule time slots overlap
    for (const day of this.ORDERED_WEEK_DAYS) {
      const schedule = this.schedulerConfig[day].schedule;
      const birthdaySchedule = this.schedulerConfig[day].birthday?.schedule || [];
      if (this.timeSlotsOverlap(schedule, day) || this.timeSlotsOverlap(birthdaySchedule, day)) {
        return false;
      }
    }
    return true;
  }

  timeSlotsOverlap(schedule: ScheduleEntryModel[], day: keyof typeof WeekDaysEnum): boolean {
    for (let i = 0; i < schedule.length; i++) {
      const currentSlot = schedule[i];
      for (let j = i + 1; j < schedule.length; j++) {
        const nextSlot = schedule[j];
        if (currentSlot.startHour < nextSlot.endHour && nextSlot.startHour < currentSlot.endHour) {
          this.snackBarService.openErrorSnackBar(`Fasce orarie sovrapposte per ${WeekDaysEnum[day]}: ${currentSlot.startHour}/${currentSlot.endHour} and ${nextSlot.startHour}/${nextSlot.endHour}`);
          return true;
        }
      }
    }
    return false;
  }

  validStartHourBeforeEndHour(): boolean {
    // check if any time slot startHour is before endHour
    for (const weekDay of this.ORDERED_WEEK_DAYS) {
      const dayConfig = this.schedulerConfig[weekDay];
      const allTimeSlots = [...dayConfig.schedule];
      if (dayConfig.birthday) {
        allTimeSlots.push(...dayConfig.birthday.schedule);
      }
      for (const entry of allTimeSlots) {
        if (entry.startHour >= entry.endHour) {
          this.snackBarService.openErrorSnackBar(`La fascia oraria per ${WeekDaysEnum[weekDay]} non è valida: ${entry.startHour} deve essere prima di ${entry.endHour}`);
          return false;
        }
      }
    }
    return true;
  }
}
